import { equals } from '../Equal/dist/effect-Equal.esm.js';
import { dual, identity, constant } from '../Function/dist/effect-Function.esm.js';
import { empty as empty$1, combine as combine$1, diff as diff$1, patch as patch$1 } from './Differ/ChunkPatch.esm.js';
import { empty, combine, diff, patch } from './Differ/ContextPatch.esm.js';
import { empty as empty$2, combine as combine$2, diff as diff$2, patch as patch$2 } from './Differ/HashMapPatch.esm.js';
import { empty as empty$3, combine as combine$3, diff as diff$3, patch as patch$3 } from './Differ/HashSetPatch.esm.js';
import { empty as empty$4, combine as combine$4, diff as diff$4, patch as patch$4 } from './Differ/OrPatch.esm.js';

/** @internal */
const DifferTypeId = /*#__PURE__*/Symbol.for("effect/Differ");

/** @internal */
const DifferProto = {
  [DifferTypeId]: {
    _P: identity,
    _V: identity
  }
};

/** @internal */
const make = params => {
  const differ = Object.create(DifferProto);
  differ.empty = params.empty;
  differ.diff = params.diff;
  differ.combine = params.combine;
  differ.patch = params.patch;
  return differ;
};

/** @internal */
const environment = () => make({
  empty: empty(),
  combine: (first, second) => combine(second)(first),
  diff: (oldValue, newValue) => diff(oldValue, newValue),
  patch: (patch$1, oldValue) => patch(oldValue)(patch$1)
});

/** @internal */
const chunk = differ => make({
  empty: empty$1(),
  combine: (first, second) => combine$1(second)(first),
  diff: (oldValue, newValue) => diff$1({
    oldValue,
    newValue,
    differ
  }),
  patch: (patch, oldValue) => patch$1(oldValue, differ)(patch)
});

/** @internal */
const hashMap = differ => make({
  empty: empty$2(),
  combine: (first, second) => combine$2(second)(first),
  diff: (oldValue, newValue) => diff$2({
    oldValue,
    newValue,
    differ
  }),
  patch: (patch, oldValue) => patch$2(oldValue, differ)(patch)
});

/** @internal */
const hashSet = () => make({
  empty: empty$3(),
  combine: (first, second) => combine$3(second)(first),
  diff: (oldValue, newValue) => diff$3(oldValue, newValue),
  patch: (patch, oldValue) => patch$3(oldValue)(patch)
});

/** @internal */
const orElseEither = /*#__PURE__*/dual(2, (self, that) => make({
  empty: empty$4(),
  combine: (first, second) => combine$4(first, second),
  diff: (oldValue, newValue) => diff$4({
    oldValue,
    newValue,
    left: self,
    right: that
  }),
  patch: (patch, oldValue) => patch$4(patch, {
    oldValue,
    left: self,
    right: that
  })
}));

/** @internal */
const transform = /*#__PURE__*/dual(2, (self, {
  toNew,
  toOld
}) => make({
  empty: self.empty,
  combine: (first, second) => self.combine(first, second),
  diff: (oldValue, newValue) => self.diff(toOld(oldValue), toOld(newValue)),
  patch: (patch, oldValue) => toNew(self.patch(patch, toOld(oldValue)))
}));

/** @internal */
const update = () => updateWith((_, a) => a);

/** @internal */
const updateWith = f => make({
  empty: identity,
  combine: (first, second) => {
    if (first === identity) {
      return second;
    }
    if (second === identity) {
      return first;
    }
    return a => second(first(a));
  },
  diff: (oldValue, newValue) => {
    if (equals(oldValue, newValue)) {
      return identity;
    }
    return constant(newValue);
  },
  patch: (patch, oldValue) => f(oldValue, patch(oldValue))
});

/** @internal */
const zip = /*#__PURE__*/dual(2, (self, that) => make({
  empty: [self.empty, that.empty],
  combine: (first, second) => [self.combine(first[0], second[0]), that.combine(first[1], second[1])],
  diff: (oldValue, newValue) => [self.diff(oldValue[0], newValue[0]), that.diff(oldValue[1], newValue[1])],
  patch: (patch, oldValue) => [self.patch(patch[0], oldValue[0]), that.patch(patch[1], oldValue[1])]
}));

export { DifferProto, DifferTypeId, chunk, environment, hashMap, hashSet, make, orElseEither, transform, update, updateWith, zip };
